// src/components/ApolloEnrichModal.js
// AÇÃO 3 HANDOFF 21/abr/2026: UI para disparar enriquecimento Apollo em massa.
//
// Estilo alinhado com DominiosNovosModal (Tailwind + lucide-react).
// Consome o hook useApolloEnrich e exibe o resumo do batch após execução.

import React, { useState } from 'react';
import {
  X, RefreshCw, AlertCircle, CheckCircle2, XCircle, SkipForward, Search, Eye,
} from 'lucide-react';
import { useApolloEnrich } from '../hooks/useApolloEnrich';
import { useAgentHistory } from '../hooks/useAgentHistory';
import AgentDecisionBanner from './AgentDecisionBanner';
import { supabase } from '../supabaseClient';

// FASE 1: painel expandido "ajudar" — sub-componente para poder chamar
// useAgentHistory por linha sem violar Rule of Hooks (hook em loop).
const HelperPanel = ({
  r,
  busy,
  hintInput,
  setHintInput,
  aiSuggestion,
  feedback,
  overrideConfirmed,
  setOverrideConfirmed,
  onAiResolve,
  onHintSubmit,
  onSkip,
}) => {
  const { lastDecision } = useAgentHistory(r.empresa_id);
  const isBlockedBySkip = lastDecision?.recommendation === 'skip' && !overrideConfirmed;

  return (
    <div className="border-t border-slate-200 bg-slate-50 p-3">
      <AgentDecisionBanner
        lastDecision={lastDecision}
        overrideConfirmed={overrideConfirmed}
        onOverrideConfirm={() => setOverrideConfirmed(true)}
      />

      {/* AÇÃO 6: botão AI-first no topo */}
      <button
        onClick={() => onAiResolve(r)}
        disabled={busy || isBlockedBySkip}
        title={isBlockedBySkip ? 'Agente já decidiu skip. Clique em "Discordar e forçar" acima para habilitar.' : ''}
        className={`w-full mb-3 px-3 py-2 text-sm rounded flex items-center justify-center gap-2 font-medium ${
          isBlockedBySkip
            ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
            : 'bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed'
        }`}
      >
        {busy && !aiSuggestion ? (
          <>
            <span className="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full"></span>
            <span>Agente analisando CRM + web...</span>
          </>
        ) : (
          <>🤖 Resolver automaticamente com IA</>
        )}
      </button>

      {aiSuggestion && !aiSuggestion.applied && (
        <div className="mb-3 p-2.5 bg-violet-50 border border-violet-200 rounded">
          <div className="flex items-baseline justify-between mb-1">
            <strong className="text-xs text-violet-900">
              🤖 Sugestão do agente: {aiSuggestion.recommendation}
            </strong>
            <span className="text-[10px] text-violet-700 font-mono">
              {Math.round(aiSuggestion.confidence * 100)}% conf.
            </span>
          </div>
          <p className="text-[11px] text-violet-800 leading-relaxed mb-2">
            {aiSuggestion.reasoning}
          </p>
          {aiSuggestion.evidence_chain?.length > 0 && (
            <details className="text-[10px] text-violet-700">
              <summary className="cursor-pointer hover:text-violet-900">
                ver evidence chain ({aiSuggestion.evidence_chain.length} sinais)
              </summary>
              <ul className="mt-1 ml-3 space-y-0.5">
                {aiSuggestion.evidence_chain.map((ev, idx) => (
                  <li key={idx}>
                    <code className="text-slate-600">{ev.source}</code>: {ev.signal}{' '}
                    <span className="text-slate-400">(peso {ev.weight})</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
          {aiSuggestion.web_searches_used > 0 && (
            <p className="mt-1 text-[10px] text-violet-600">
              🔍 usou {aiSuggestion.web_searches_used} web search(es)
            </p>
          )}
        </div>
      )}

      <div className="text-[10px] text-slate-400 mb-2 text-center">— ou resolver manualmente —</div>

      <label className="block text-xs text-slate-600 mb-1">
        Cole <strong>uma</strong> das opções — detecto automaticamente:
      </label>
      <input
        type="text"
        value={hintInput}
        onChange={(e) => setHintInput(e.target.value)}
        placeholder="https://app.apollo.io/#/accounts/... · LinkedIn · domínio · nome completo"
        className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded mb-2 disabled:bg-slate-100"
        disabled={busy || isBlockedBySkip}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !busy && !isBlockedBySkip && hintInput.trim()) onHintSubmit(r, lastDecision);
        }}
      />

      <div className="text-[11px] text-slate-500 mb-2 leading-relaxed">
        <strong>Exemplos:</strong>{' '}
        <code className="text-slate-700">app.apollo.io/#/accounts/5e1624ba...</code> → apollo_id direto;{' '}
        <code className="text-slate-700">linkedin.com/company/bko</code>;{' '}
        <code className="text-slate-700">bko.com.br</code>;{' '}
        <code className="text-slate-700">BKO Empreendimentos Imobiliários</code>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onHintSubmit(r, lastDecision)}
          disabled={busy || isBlockedBySkip || !hintInput.trim()}
          className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? '...' : '🔍 Buscar e aplicar'}
        </button>
        <button
          onClick={() => onSkip(r)}
          disabled={busy || isBlockedBySkip}
          className="flex-1 px-3 py-1.5 text-sm border border-slate-300 rounded hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? '...' : '⏭️ Não se aplica ao Apollo'}
        </button>
      </div>

      {overrideConfirmed && (
        <div className="mt-2 text-[11px] text-amber-700 flex items-center gap-1">
          ⚠️ Você discordou da decisão do agente. Ações aqui serão registradas como override em <code>ai_evidence</code>.
        </div>
      )}

      {feedback && (
        <div
          className={`mt-2 text-xs px-2 py-1.5 rounded ${
            feedback.type === 'success' ? 'bg-emerald-50 text-emerald-800' :
            feedback.type === 'warning' ? 'bg-amber-50 text-amber-800' :
            'bg-red-50 text-red-800'
          }`}
        >
          {feedback.message}
        </div>
      )}

      <p className="mt-2 text-[10px] text-slate-400">
        Cada ação gera evidência em <code>ai_evidence</code> para auditoria — aparece no F8 se conflitar.
      </p>
    </div>
  );
};

// Metadados por status retornado pela edge function
const STATUS_META = {
  committed: { icon: CheckCircle2, iconClass: 'text-emerald-600', label: '✅ Enriquecida',  rowClass: 'bg-emerald-50' },
  preview:   { icon: Eye,          iconClass: 'text-blue-600',    label: '👁️ Preview',     rowClass: 'bg-blue-50'    },
  skipped:   { icon: SkipForward,  iconClass: 'text-slate-400',   label: '⏭️ Já tinha',    rowClass: 'bg-white'      },
  no_match:  { icon: Search,       iconClass: 'text-slate-400',   label: '🔍 Sem match',   rowClass: 'bg-white'      },
  error:     { icon: XCircle,      iconClass: 'text-red-600',     label: '❌ Erro',        rowClass: 'bg-red-50'     },
};

const ApolloEnrichModal = ({ onClose, userEmail, onEnrichmentSuccess }) => {
  const { invoke, loading, result, error, reset } = useApolloEnrich();
  const [limit, setLimit] = useState(10);
  const [forceRefresh, setForceRefresh] = useState(false);
  const [mode, setMode] = useState('commit'); // 'commit' (default) | 'dry_run'

  // AÇÃO 5: ajuda humana inline para linhas "sem match"
  const [expandedEmpresaId, setExpandedEmpresaId] = useState(null);
  const [hintInput, setHintInput] = useState('');
  const [actionInFlight, setActionInFlight] = useState(null); // empresa_id
  const [rowFeedback, setRowFeedback] = useState({});         // { [empresa_id]: {type,message} }
  // AÇÃO 6: resultado do agente IA por empresa
  // { [empresa_id]: { applied, recommendation, confidence, reasoning, evidence_chain, hints, skip_reason, apollo_name } }
  const [aiSuggestions, setAiSuggestions] = useState({});
  // FASE 1: humano clicou "Discordar e forçar" para habilitar override de skip do agente
  const [overrideConfirmedMap, setOverrideConfirmedMap] = useState({}); // { [empresa_id]: true }

  const handleEnrich = () => {
    invoke({
      limit: Math.min(Math.max(1, Number(limit) || 1), 50),
      force_refresh: forceRefresh,
      mode,
    });
  };

  const handleReset = () => {
    reset();
  };

  // Detecta formato do hint colado pelo usuário
  const parseHint = (raw) => {
    const s = (raw || '').trim();
    if (!s) return null;

    const apolloMatch = s.match(/app\.apollo\.io\/#?\/accounts\/([a-f0-9]{20,})/i);
    if (apolloMatch) return { kind: 'apollo_id', hint_apollo_id: apolloMatch[1] };

    if (/^[a-f0-9]{20,30}$/i.test(s)) return { kind: 'apollo_id', hint_apollo_id: s };

    if (/linkedin\.com\/company\//i.test(s)) {
      const url = s.startsWith('http') ? s : `https://${s}`;
      return { kind: 'linkedin', hint_linkedin_url: url };
    }

    if (!s.includes(' ') && /^(https?:\/\/)?[a-z0-9-]+(\.[a-z0-9-]+)+\/?.*$/i.test(s)) {
      const d = s
        .replace(/^https?:\/\//i, '')
        .replace(/\/.*$/, '')
        .replace(/^www\./i, '')
        .toLowerCase();
      return { kind: 'domain', hint_domain: d };
    }

    return { kind: 'name', hint_name: s };
  };

  const handleHintSubmit = async (r, lastDecision) => {
    const parsed = parseHint(hintInput);
    if (!parsed) {
      setRowFeedback((p) => ({
        ...p,
        [r.empresa_id]: { type: 'error', message: 'Digite uma URL Apollo, LinkedIn, domínio ou nome.' },
      }));
      return;
    }

    // FASE 1: confirmação extra quando humano está desfazendo decisão de skip do agente
    if (lastDecision?.recommendation === 'skip') {
      const when = new Date(lastDecision.created_at).toLocaleDateString('pt-BR');
      const reasonTxt = lastDecision.skip_reason || lastDecision.reasoning || '(sem motivo registrado)';
      // eslint-disable-next-line no-alert
      const proceed = window.confirm(
        `⚠️ Atenção: o agente IA classificou esta empresa como NÃO-APLICÁVEL em ${when}.\n\n` +
        `Motivo registrado: "${reasonTxt}"\n\n` +
        `Aplicar este hint vai:\n` +
        `• Remover a marcação "não-aplicável"\n` +
        `• Aplicar apollo_id se o nome bater por similaridade\n` +
        `• Registrar override explícito em ai_evidence para auditoria\n\n` +
        `Prosseguir?`
      );
      if (!proceed) return;
    }

    setActionInFlight(r.empresa_id);
    setRowFeedback((p) => ({ ...p, [r.empresa_id]: null }));

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('enrich-companies-via-apollo', {
        body: {
          target_empresa_id: r.empresa_id,
          hint_apollo_id: parsed.hint_apollo_id,
          hint_linkedin_url: parsed.hint_linkedin_url,
          hint_domain: parsed.hint_domain,
          hint_name: parsed.hint_name,
          decided_by: userEmail || 'usuario-desconhecido',
          mode: 'commit',
        },
      });
      if (fnErr) throw fnErr;
      if (data?.error) throw new Error(data.error);

      const hit = data?.results?.[0];

      // FASE 1: rejected_by_similarity — Apollo devolveu algo mas nome não bate
      if (hit?.rejected_by_similarity && hit.rejected_by_similarity.length > 0) {
        const rej = hit.rejected_by_similarity[0];
        setRowFeedback((p) => ({
          ...p,
          [r.empresa_id]: {
            type: 'warning',
            message: `🚫 Apollo retornou "${rej.apollo_name}" mas nome não bate com "${rej.crm_name}". Match rejeitado por segurança. Verifique se é a empresa certa ou use apollo_id direto.`,
          },
        }));
      } else if (hit?.apollo_id) {
        const credits = data.apollo_credits_used;
        const creditsLabel = typeof credits === 'number'
          ? `, ${credits} crédito${credits === 1 ? '' : 's'}`
          : '';
        // FASE 1: distingue unskip (reverteu decisão do agente) de match normal
        const prefix = hit.unskip_performed
          ? `🔄 Override aplicado: `
          : `✅ Enriquecida: `;
        const suffix = hit.unskip_performed
          ? ` Flag "não-aplicável" removida, match registrado em F8.`
          : ` (${hit.match_strategy}${creditsLabel})`;
        setRowFeedback((p) => ({
          ...p,
          [r.empresa_id]: {
            type: 'success',
            message: `${prefix}${hit.apollo_name || hit.nome}${suffix}`,
          },
        }));
        setTimeout(() => {
          setExpandedEmpresaId(null);
          setHintInput('');
        }, 2500);
        if (typeof onEnrichmentSuccess === 'function') onEnrichmentSuccess(r.empresa_id);
      } else {
        setRowFeedback((p) => ({
          ...p,
          [r.empresa_id]: {
            type: 'warning',
            message: `⚠️ Hint aplicado mas Apollo não achou: ${hit?.match_strategy || 'sem match'}. Tente outro formato.`,
          },
        }));
      }
    } catch (err) {
      setRowFeedback((p) => ({
        ...p,
        [r.empresa_id]: { type: 'error', message: `Erro: ${err.message || String(err)}` },
      }));
    } finally {
      setActionInFlight(null);
    }
  };

  const handleSkip = async (r) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Marcar "${r.nome}" como "não se aplica ao Apollo"? Ela deixará de aparecer no scan.`)) {
      return;
    }

    setActionInFlight(r.empresa_id);
    setRowFeedback((p) => ({ ...p, [r.empresa_id]: null }));

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('mark-apollo-not-applicable', {
        body: {
          empresa_id: r.empresa_id,
          reason: 'fora do escopo Apollo (marcado via modal de enriquecimento)',
          decided_by: userEmail || 'usuario-desconhecido',
        },
      });
      if (fnErr) throw fnErr;
      if (data?.ok === false) throw new Error(data?.error || 'falha ao marcar');

      setRowFeedback((p) => ({
        ...p,
        [r.empresa_id]: {
          type: 'success',
          message: '⏭️ Marcada como não se aplica. Não aparecerá mais.',
        },
      }));
      setTimeout(() => {
        setExpandedEmpresaId(null);
        setHintInput('');
      }, 2000);
      if (typeof onEnrichmentSuccess === 'function') onEnrichmentSuccess(r.empresa_id);
    } catch (err) {
      setRowFeedback((p) => ({
        ...p,
        [r.empresa_id]: { type: 'error', message: `Erro: ${err.message || String(err)}` },
      }));
    } finally {
      setActionInFlight(null);
    }
  };

  // AÇÃO 6: invoca o agente IA que analisa contexto interno + web search e decide/aplica
  const handleAiResolve = async (r) => {
    setActionInFlight(r.empresa_id);
    setRowFeedback((p) => ({ ...p, [r.empresa_id]: null }));
    setAiSuggestions((p) => ({ ...p, [r.empresa_id]: null }));

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('agent-apollo-context-harvester', {
        body: {
          empresa_id: r.empresa_id,
          decided_by: userEmail || 'usuario-desconhecido',
          auto_apply: true,
        },
      });
      if (fnErr) throw fnErr;
      if (data?.ok === false) throw new Error(data?.error || 'falha no agente');

      setAiSuggestions((p) => ({ ...p, [r.empresa_id]: data }));

      if (data.applied) {
        const detalhe = data.downstream_action === 'skip'
          ? `⏭️ Skip automático: ${data.skip_reason || 'não se aplica ao Apollo'}`
          : `✅ Enriquecida: ${data.apollo_name} (${Math.round(data.confidence * 100)}% confiança)`;
        setRowFeedback((p) => ({
          ...p,
          [r.empresa_id]: { type: 'success', message: `🤖 ${detalhe}` },
        }));
        setTimeout(() => {
          setExpandedEmpresaId(null);
          setHintInput('');
        }, 2500);
        if (typeof onEnrichmentSuccess === 'function') onEnrichmentSuccess(r.empresa_id);
      } else {
        const topHint =
          data.hints?.hint_apollo_id ||
          data.hints?.hint_linkedin_url ||
          data.hints?.hint_domain ||
          data.hints?.hint_name ||
          '';
        if (topHint) setHintInput(topHint);

        setRowFeedback((p) => ({
          ...p,
          [r.empresa_id]: {
            type: 'warning',
            message: `🤖 Agente sugere ${data.recommendation} (${Math.round(data.confidence * 100)}% confiança) — revise abaixo e decida.`,
          },
        }));
      }
    } catch (err) {
      setRowFeedback((p) => ({
        ...p,
        [r.empresa_id]: { type: 'error', message: `Agente falhou: ${err.message || String(err)}` },
      }));
    } finally {
      setActionInFlight(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-40 flex items-start justify-center pt-8 px-4">
      <div className="bg-slate-50 rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white rounded-t-lg">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Enriquecimento</div>
            <h2 className="font-bold text-slate-800 flex items-center gap-2">
              🟢 Enriquecer empresas via Apollo
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Busca dados de indústria, funcionários, receita e keywords para
              empresas que ainda não foram enriquecidas.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={20} />
          </button>
        </div>

        {/* Formulário de parâmetros */}
        <div className="px-4 py-3 border-b bg-white space-y-3">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="block text-xs text-slate-600 mb-1">Limite</label>
              <input
                type="number"
                min={1}
                max={50}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                className="w-20 text-sm px-2 py-1 border border-slate-300 rounded"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-xs text-slate-600 mb-1">Modo</label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                disabled={loading}
                className="text-sm px-2 py-1 border border-slate-300 rounded bg-white"
              >
                <option value="commit">🟢 Commit (persiste)</option>
                <option value="dry_run">👁️ Dry run (só preview)</option>
              </select>
            </div>

            <label className="flex items-center gap-2 text-xs text-slate-700 pb-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={forceRefresh}
                onChange={(e) => setForceRefresh(e.target.checked)}
                disabled={loading}
                className="w-4 h-4"
              />
              <span>
                Reprocessar já enriquecidas
                <span className="block text-[11px] text-slate-500">
                  Consome mais créditos Apollo.
                </span>
              </span>
            </label>

            <div className="flex-1" />

            {!result && !error && (
              <button
                onClick={handleEnrich}
                disabled={loading}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-1.5 text-sm font-semibold disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <RefreshCw size={14} className="animate-spin" /> Processando...
                  </>
                ) : (
                  <>
                    🟢 Enriquecer
                  </>
                )}
              </button>
            )}

            {(result || error) && !loading && (
              <button
                onClick={handleReset}
                className="px-3 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 flex items-center gap-1.5 text-sm"
              >
                <RefreshCw size={14} /> Nova rodada
              </button>
            )}
          </div>
        </div>

        {/* Corpo: resultado / erro / vazio */}
        <div className="flex-1 overflow-auto p-4">
          {loading && (
            <div className="text-sm text-slate-500 py-8 text-center">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
              Consultando Apollo... (pode levar alguns segundos por empresa)
            </div>
          )}

          {!loading && error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3 flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>Erro: {error}</span>
            </div>
          )}

          {!loading && !error && !result && (
            <div className="text-sm text-slate-500 italic py-8 text-center bg-white rounded border border-slate-200">
              Configure os parâmetros acima e clique em "Enriquecer" para iniciar.
            </div>
          )}

          {!loading && !error && result && (
            <>
              {/* Resumo */}
              <div className="bg-white border border-slate-200 rounded-lg p-3 mb-3">
                <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Resumo · modo <span className="font-mono">{result.mode}</span>
                  {typeof result.duration_ms === 'number' && (
                    <span className="ml-2 text-slate-400">
                      ({(result.duration_ms / 1000).toFixed(1)}s)
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 flex-wrap text-sm">
                  <span>
                    <strong>{result.total ?? 0}</strong> processadas
                  </span>
                  <span className="text-emerald-700">
                    ✅ <strong>{result.matched ?? 0}</strong> {result.mode === 'dry_run' ? 'preview' : 'enriquecidas'}
                  </span>
                  <span className="text-slate-500">
                    🔍 <strong>{result.not_matched ?? 0}</strong> sem match
                  </span>
                  <span className="text-red-700">
                    ❌ <strong>{result.errors ?? 0}</strong> falhas
                  </span>
                </div>
                {result.warning && (
                  <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    {result.warning}
                  </div>
                )}
              </div>

              {/* Lista de empresas processadas */}
              {Array.isArray(result.results) && result.results.length > 0 && (
                <ul className="space-y-1">
                  {result.results.map((r, i) => {
                    const meta = STATUS_META[r.status] || {
                      icon: AlertCircle, iconClass: 'text-slate-500',
                      label: r.status || '—', rowClass: 'bg-white',
                    };
                    const Icon = meta.icon;
                    const isNoMatch = r.status === 'no_match';
                    const isExpanded = expandedEmpresaId === r.empresa_id;
                    const feedback = rowFeedback[r.empresa_id];
                    const busy = actionInFlight === r.empresa_id;

                    return (
                      <li
                        key={r.empresa_id || i}
                        className={`${meta.rowClass} border border-slate-200 rounded text-sm`}
                      >
                        <div
                          className="px-3 py-2 flex items-center gap-2"
                          title={r.error || r.match_strategy || ''}
                        >
                          <Icon size={14} className={`flex-shrink-0 ${meta.iconClass}`} />
                          <span className="flex-1 min-w-0 truncate text-slate-800">
                            {r.nome || '(sem nome)'}
                          </span>
                          <span className="text-xs text-slate-500 whitespace-nowrap">
                            {meta.label}
                            {r.match_strategy && (
                              <span className="ml-1 font-mono text-slate-400">
                                ({r.match_strategy})
                              </span>
                            )}
                          </span>
                          {isNoMatch && (
                            <button
                              onClick={() => {
                                setExpandedEmpresaId(isExpanded ? null : r.empresa_id);
                                setHintInput('');
                                setRowFeedback((p) => ({ ...p, [r.empresa_id]: null }));
                              }}
                              disabled={busy}
                              className="text-[11px] px-2 py-0.5 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-50 flex-shrink-0"
                            >
                              {isExpanded ? 'fechar ▴' : 'ajudar ▾'}
                            </button>
                          )}
                        </div>

                        {isNoMatch && isExpanded && (
                          <HelperPanel
                            r={r}
                            busy={busy}
                            hintInput={hintInput}
                            setHintInput={setHintInput}
                            aiSuggestion={aiSuggestions[r.empresa_id]}
                            feedback={feedback}
                            overrideConfirmed={!!overrideConfirmedMap[r.empresa_id]}
                            setOverrideConfirmed={(v) =>
                              setOverrideConfirmedMap((p) => ({ ...p, [r.empresa_id]: v }))
                            }
                            onAiResolve={handleAiResolve}
                            onHintSubmit={handleHintSubmit}
                            onSkip={handleSkip}
                          />
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t bg-white rounded-b-lg text-xs text-slate-500 flex items-center justify-between">
          <span>
            Edge function: <span className="font-mono">enrich-companies-via-apollo</span>
          </span>
          <button
            onClick={onClose}
            className="px-3 py-1 bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};

export default ApolloEnrichModal;
